import {
    Input,
    Autocomplete,
    AutocompleteItem,
    NumberInput,
    Select,
    SelectItem,
} from "@heroui/react";
import { useFetchData } from "@/hooks";
import { FilterCard } from "@/components/data-table";
import { ThemedText } from "@/components/ui";

/**
 * Filter component for Products page
 *
 * @param {Object} filterOptions - Options for select filters (passed from parent)
 *   - categories: Array of { value, title }
 *   - units: Array of { value, title }
 *   - statuses: Array of { value, title }
 *
 * Note: subCategories are fetched internally based on selected category
 */
const FilterData = ({ table, initialFilters, filterOptions = {} }) => {
    const { filterPanel } = table;
    const { isOpen, pending, setPending, apply, clear } = filterPanel;

    const { categories = [], units = [], statuses = [] } = filterOptions;

    // Fetch sub categories based on selected category in pending values (dynamic)
    const { data: subCategories = [], isLoading: isSubCategoriesLoading } =
        useFetchData(
            pending.category_id
                ? `select/material-sub-category?category_id=${pending.category_id}`
                : null,
        );

    // Get selected category name for description
    const selectedCategoryName = categories.find(
        (c) => String(c.value) === String(pending.category_id),
    )?.title;

    // Handle category change - clear sub category
    const handleCategoryChange = (val) => {
        setPending((prev) => ({
            ...prev,
            category_id: val,
            sub_category_id: [],
        }));
    };

    // Handle clear - reset to initial values
    const handleClear = () => {
        setPending(initialFilters);
        clear();
    };

    return (
        <FilterCard isVisible={isOpen} onClear={handleClear} onApply={apply}>
            {/* Nama Produk */}
            <Input
                label="Nama Produk"
                placeholder="Cari nama produk..."
                value={pending.name || ""}
                onValueChange={(val) =>
                    setPending((prev) => ({
                        ...prev,
                        name: val,
                    }))
                }
                variant="flat"
                isClearable
            />

            {/* Kategori */}
            <Autocomplete
                label="Kategori"
                placeholder="Pilih kategori"
                selectedKey={
                    pending.category_id ? String(pending.category_id) : null
                }
                onSelectionChange={handleCategoryChange}
                variant="flat"
                isClearable
            >
                {categories.map((c) => (
                    <AutocompleteItem key={String(c.value)} value={c.value}>
                        {c.title}
                    </AutocompleteItem>
                ))}
            </Autocomplete>

            {/* Sub Kategori - only show when category selected, multiple selection */}
            {pending.category_id && (
                <Select
                    label="Sub Kategori"
                    placeholder="Pilih sub kategori"
                    description={`Dalam kategori: ${selectedCategoryName}`}
                    selectionMode="multiple"
                    selectedKeys={
                        new Set(
                            Array.isArray(pending.sub_category_id)
                                ? pending.sub_category_id.map(String)
                                : [],
                        )
                    }
                    onSelectionChange={(keys) =>
                        setPending((prev) => ({
                            ...prev,
                            sub_category_id: Array.from(keys),
                        }))
                    }
                    variant="flat"
                    isLoading={isSubCategoriesLoading}
                >
                    {subCategories.map((c) => (
                        <SelectItem key={String(c.value)} value={c.value}>
                            {c.title}
                        </SelectItem>
                    ))}
                </Select>
            )}

            {/* Satuan - multiple selection */}
            <Select
                label="Satuan"
                placeholder="Pilih satuan"
                selectionMode="multiple"
                selectedKeys={
                    new Set(
                        Array.isArray(pending.unit_id)
                            ? pending.unit_id.map(String)
                            : [],
                    )
                }
                onSelectionChange={(keys) =>
                    setPending((prev) => ({
                        ...prev,
                        unit_id: Array.from(keys),
                    }))
                }
                variant="flat"
            >
                {units.map((u) => (
                    <SelectItem key={String(u.value)} value={u.value}>
                        {u.title}
                    </SelectItem>
                ))}
            </Select>

            {/* Rentang Harga */}
            <div className="flex items-center gap-2">
                <NumberInput
                    label="Harga Min"
                    placeholder="Contoh: 15000"
                    value={
                        pending.price_min
                            ? Number(pending.price_min)
                            : undefined
                    }
                    onValueChange={(val) =>
                        setPending((prev) => ({
                            ...prev,
                            price_min: val ? String(val) : "",
                        }))
                    }
                    isClearable
                    variant="flat"
                    hideStepper
                    startContent={
                        <ThemedText size="sm" variant="secondary">
                            Rp
                        </ThemedText>
                    }
                    formatOptions={{
                        style: "decimal",
                        maximumFractionDigits: 0,
                    }}
                />
                <span className="text-default-400">-</span>
                <NumberInput
                    label="Harga Max"
                    placeholder="0"
                    value={
                        pending.price_max
                            ? Number(pending.price_max)
                            : undefined
                    }
                    onValueChange={(val) =>
                        setPending((prev) => ({
                            ...prev,
                            price_max: val ? String(val) : "",
                        }))
                    }
                    minValue={
                        pending.price_min
                            ? Number(pending.price_min)
                            : undefined
                    }
                    isClearable
                    variant="flat"
                    hideStepper
                    startContent={
                        <ThemedText size="sm" variant="secondary">
                            Rp
                        </ThemedText>
                    }
                    formatOptions={{
                        style: "decimal",
                        maximumFractionDigits: 0,
                    }}
                />
            </div>

            {/* Status - multiple selection */}
            <Select
                label="Status"
                placeholder="Pilih status"
                selectionMode="multiple"
                selectedKeys={
                    new Set(
                        Array.isArray(pending.active_status_id)
                            ? pending.active_status_id.map(String)
                            : [],
                    )
                }
                onSelectionChange={(keys) =>
                    setPending((prev) => ({
                        ...prev,
                        active_status_id: Array.from(keys),
                    }))
                }
                variant="flat"
            >
                {statuses.map((s) => (
                    <SelectItem key={String(s.value)} value={s.value}>
                        {s.title}
                    </SelectItem>
                ))}
            </Select>
        </FilterCard>
    );
};

export default FilterData;
